import {
  FC,
  PropsWithChildren,
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { POSITION, SIZE } from 'utils/constants';

import { ModalWrapper } from './Modal';

type ModalSize = SIZE | 'xl' | 'paper';

interface ShowModalArgs {
  content: ReactNode;
  title?: string;
  disableOutsideClick?: boolean;
  hideHeader?: boolean;
  size?: ModalSize;
  position?: POSITION;
  closeCallback?: () => void;
  resetPadding?: boolean;
}

interface ModalInterface {
  showModal: ({
    content,
    title,
    disableOutsideClick,
    hideHeader,
    size,
    position,
    resetPadding,
  }: ShowModalArgs) => void;
  hideModal: () => void;
  open: boolean;
}

export const ExternalModalContext = createContext<ModalInterface>({
  hideModal: () => {},
  showModal: () => {},
  open: false,
});

const ExternalModal: FC<PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSize, setModalSize] = useState<ModalSize>(SIZE.Large);
  const [modalPosition, setModalPosition] = useState<POSITION>(POSITION.Center);
  const [modalContent, setModalContent] = useState<ReactNode | null>(null);
  const [hideModalHeader, setHideModalHeader] = useState(false);
  const [modalDisableOutsideClick, setDisableOutsideClick] = useState(false);
  const [closeCallback, setCloseCallback] = useState(() => () => {});
  const [resetPadding, setResetPadding] = useState<boolean | undefined>(false);

  const reset = () => {
    setHideModalHeader(false);
    setDisableOutsideClick(false);
    setModalTitle('');
    setModalSize(SIZE.Large);
    setModalPosition(POSITION.Center);
    setModalContent(null);
    setCloseCallback(() => () => {});
  };

  const showModal = useCallback(
    ({
      content,
      title = '',
      disableOutsideClick = false,
      hideHeader = false,
      size = SIZE.Large,
      position = POSITION.Center,
      resetPadding,
      closeCallback = () => {},
    }: ShowModalArgs) => {
      setModalPosition(position);
      setModalTitle(title);
      setModalSize(size);
      setDisableOutsideClick(disableOutsideClick);
      setHideModalHeader(hideHeader);
      setModalContent(content);
      setCloseCallback(() => closeCallback);
      setResetPadding(resetPadding);
    },
    [],
  );

  const hideModal = useCallback(() => {
    setOpen(false);
    closeCallback();

    // reset after transition duration
    setTimeout(reset, 201);
  }, [closeCallback]);

  useEffect(() => {
    setOpen(!!modalContent);
  }, [modalContent]);

  const value = useMemo(
    () => ({ showModal, hideModal, open }),
    [showModal, hideModal, open],
  );

  return (
    <ExternalModalContext.Provider value={value}>
      <ModalWrapper
        className="z-[999]"
        disableOutsideClick={modalDisableOutsideClick}
        isOpen={open}
        closeModal={hideModal}
        content={modalContent}
        hideHeader={hideModalHeader}
        title={modalTitle}
        size={modalSize}
        position={modalPosition}
        resetPadding={resetPadding}
      />
      {children}
    </ExternalModalContext.Provider>
  );
};

export default ExternalModal;
